import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { UserService } from '../../../services/user.service';
import { ALIANZA_CARRUSEL_MS } from '../../data/perfil-alianza.content';
import { PERFIL_HUB_ROUTE } from '../progress/perfil-section-nav';
import { AlianzaCarruselFade } from './perfil-alianza-carrusel-fade';
import { sanitizeAlianzaHtml } from './perfil-alianza-html.util';
import { AlianzaEstrategica } from './perfil-alianza.model';
import { PerfilAlianzaService } from './perfil-alianza.service';
import { AnalyticsTagService } from 'src/app/services/analytics-tag.service';

/**
 * HU 8338 — landing alianza (Figma 33147 / 33191).
 */
@Component({
  selector: 'app-perfil-alianza',
  templateUrl: './perfil-alianza.component.html',
  styleUrls: ['./perfil-alianza.component.scss'],
  standalone: false,
})
export class PerfilAlianzaComponent implements OnInit, OnDestroy {
  userName = '';
  loading = true;
  alianzas: AlianzaEstrategica[] = [];
  indice = 0;

  readonly fade = new AlianzaCarruselFade();

  private sub?: Subscription;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private hoverPaused = false;
  private focusPaused = false;

  constructor(
    private readonly userService: UserService,
    private readonly alianzaService: PerfilAlianzaService,
    private readonly router: Router,
    private readonly sanitizer: DomSanitizer,
    private readonly cdr: ChangeDetectorRef,
    private readonly analyticsTag: AnalyticsTagService
  ) {}

  ngOnInit(): void {
    this.userName = this.userService.getUser()?.nombre ?? '';
    this.sub = this.alianzaService.loadActivas().subscribe((lista) => {
      if (!lista.length) {
        void this.router.navigateByUrl(PERFIL_HUB_ROUTE);
        return;
      }
      this.alianzas = lista;
      this.indice = 0;
      this.loading = false;
      this.restartTimer();
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.stopTimer();
    this.fade.destroy();
  }

  get actual(): AlianzaEstrategica | null {
    return this.alianzas[this.indice] ?? this.alianzas[0] ?? null;
  }

  get mostrarCarrusel(): boolean {
    return this.alianzas.length > 1;
  }

  html(value: string | null | undefined): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(sanitizeAlianzaHtml(value));
  }

  onCta(): void {
    const url = this.actual?.landingUrlForward || this.actual?.urlForward;
    if (!url) {
      return;
    }
    this.analyticsTag.trackCtaClick({
      ctaLabel: this.actual?.landingNombreBoton ?? '',
      ctaSection: 'alianza-landing',
      ctaComponent: 'perfil-alianza',
      ctaElementId: 'btn-alianza-landing-cta',
      hu: '8338',
    });
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  onOmitir(): void {
    this.analyticsTag.trackCtaClick({
      ctaLabel: this.actual?.textoOmitir ?? '',
      ctaSection: 'alianza-landing',
      ctaComponent: 'perfil-alianza',
      ctaElementId: 'btn-alianza-omitir',
      hu: '8338',
    });

    const siguienteIndice = this.indice + 1;
    if (siguienteIndice < this.alianzas.length) {
      this.cambiarIndice(siguienteIndice);
      this.restartTimer();
      return;
    }

    void this.router.navigateByUrl(PERFIL_HUB_ROUTE);
  }

  irA(indice: number): void {
    if (indice < 0 || indice >= this.alianzas.length) {
      return;
    }
    this.cambiarIndice(indice);
    this.restartTimer();
  }

  onCarruselPause(): void {
    this.hoverPaused = true;
    this.stopTimer();
  }

  onCarruselResume(): void {
    this.hoverPaused = false;
    this.restartTimer();
  }

  onCarruselFocusIn(): void {
    this.focusPaused = true;
    this.stopTimer();
  }

  onCarruselFocusOut(): void {
    this.focusPaused = false;
    this.restartTimer();
  }

  private get paused(): boolean {
    return this.hoverPaused || this.focusPaused;
  }

  private cambiarIndice(next: number): void {
    this.fade.goTo(next, this.indice, (i) => {
      this.indice = i;
    }, this.cdr);
  }

  private restartTimer(): void {
    this.stopTimer();

    if (
      this.alianzas.length < 2 ||
      this.prefersReducedMotion() ||
      this.paused
    ) {
      return;
    }

    this.timer = setTimeout(() => {
      if (this.paused || this.alianzas.length < 2) {
        return;
      }

      this.cambiarIndice((this.indice + 1) % this.alianzas.length);
      this.restartTimer();
    }, ALIANZA_CARRUSEL_MS);
  }

  private stopTimer(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  private prefersReducedMotion(): boolean {
    return (
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }
}
